import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { adminLogin, adminVerify2FA, adminLogout, adminMe } from '../adminApi';

interface AdminUser {
  adminId: string;
  email: string;
  role: string;
}

interface AdminContextType {
  admin: AdminUser | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  verify2FA: (code: string) => Promise<void>;
  pending2FA: { email: string; password: string } | null;
  cancelPending2FA: () => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export function AdminProvider({ children }: { children: React.ReactNode }) {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pending2FA, setPending2FA] = useState<{ email: string; password: string } | null>(null);

  const checkAuth = useCallback(async () => {
    const storedToken = localStorage.getItem('admin_token');
    if (!storedToken) {
      setIsLoading(false);
      return;
    }
    try {
      setToken(storedToken);
      const data = await adminMe();
      setAdmin(data);
    } catch {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_refresh');
      setToken(null);
      setAdmin(null);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const applyTokens = (response: { token: string; refreshToken: string; admin: { id: string; email: string; role: string } }) => {
    setToken(response.token);
    localStorage.setItem('admin_token', response.token);
    localStorage.setItem('admin_refresh', response.refreshToken);
    setAdmin({
      adminId: response.admin.id,
      email: response.admin.email,
      role: response.admin.role,
    });
  };

  const login = async (email: string, password: string) => {
    const response = await adminLogin(email, password);

    if (response.requires2FA) {
      // Токены не выданы — запоминаем креды и ждём код 2FA
      setPending2FA({ email, password });
      return;
    }

    applyTokens(response);
  };

  const verify2FA = async (code: string) => {
    if (!pending2FA) throw new Error('Нет ожидающей 2FA-авторизации');

    const response = await adminVerify2FA(pending2FA.email, pending2FA.password, code);
    setPending2FA(null);
    applyTokens(response);
  };

  const cancelPending2FA = () => setPending2FA(null);

  const logout = () => {
    const refreshToken = localStorage.getItem('admin_refresh') || undefined;
    adminLogout(refreshToken || undefined).catch(() => {});
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_refresh');
    setToken(null);
    setAdmin(null);
  };

  return (
    <AdminContext.Provider
      value={{ admin, token, isLoading, login, verify2FA, pending2FA, cancelPending2FA, logout, isAuthenticated: !!token }}
    >
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin(): AdminContextType {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error('useAdmin must be used within AdminProvider');
  return ctx;
}
